import React, { useState } from 'react';
import { Dialog, ElevatedButton, TextButton } from '@producer/ui-kit';

import { ArrowDown } from '../icons/ArrowDown';
import {
  getPreferredTimeFormat,
  timeTo12HourFormat,
  timeTo24HourFormat,
} from '../../../lib/utils/Convertor';

import type { TypeTime } from '../../../app/entities/strip';

import styles from './TimeSelector.module.scss';
interface TimeSelectorProps {
  time: {
    hour: number;
    minute: number;
    period?: 'am' | 'pm';
  };
  onTimeChange: (time: TypeTime) => Promise<void>;
  onCancel: () => void;
}

const DEFAULT_MINUTE_INCREMENT = 15;

export const TimeSelector: React.FC<TimeSelectorProps> = ({ time, onTimeChange, onCancel }) => {
  const is24HourFormat = getPreferredTimeFormat() === '24h';
  const maxHour = is24HourFormat ? 23 : 12;

  const [hours, setHours] = useState<number>(() => {
    if (!time) {
      return 0;
    }

    return is24HourFormat && time.period ? timeTo24HourFormat(time).hour : time.hour;
  });
  const [minutes, setMinutes] = useState<string>(
    time ? time.minute.toString().padStart(2, '0') : '00',
  );
  const [period, setPeriod] = useState<'am' | 'pm'>(time && time.period ? time.period : 'am');

  const handleClear = () => {
    setHours(0);
    setMinutes('00');
    setPeriod('am');
  };

  const handleSubmitTime = async () => {
    const minute = parseInt(minutes, 10);

    // Always emit a 12-hour TypeTime so the stored shape stays the same in both display modes
    await onTimeChange(
      is24HourFormat
        ? timeTo12HourFormat({ hour: hours, minute })
        : { hour: hours, minute, period },
    );
    onCancel();
  };

  const handleTimeChange = (type: 'hours' | 'minutes') => (e: any) => {
    const value = e.target.value;
    const cleaned = value.replace(/\D/g, '');
    const numericValue = parseInt(cleaned, 10);

    if (isNaN(numericValue)) {
      return;
    }

    if (type === 'hours') {
      setHours(numericValue > maxHour ? maxHour : numericValue);
    } else {
      // Avoid leading zeros unless it's '00'
      let newMinutes = cleaned === '' ? '00' : cleaned;

      // If value exceeds 2 digits, keep only the last 2
      if (newMinutes.length > 2) {
        newMinutes = newMinutes.slice(-2);
      }

      setMinutes((numericValue > 59 ? 59 : numericValue).toString().padStart(2, '0'));
    }
  };

  const incrementHours = () => {
    if (is24HourFormat) {
      setHours((hours + 1) % 24);
      return;
    }

    const newHours = hours + 1;

    /**
     * If going from 11 to 12 or going from 12 to 11, change period
     */
    if (newHours === 12 && hours === 11) {
      changePeriod();
    }

    setHours(newHours > 12 ? 1 : newHours);
  };

  const incrementTime = (type: 'hours' | 'minutes') => () => {
    if (type === 'hours') {
      incrementHours();
    } else {
      const newMinutes = parseInt(minutes, 10) + DEFAULT_MINUTE_INCREMENT;

      if (newMinutes > 59) {
        setMinutes((-(60 - newMinutes)).toString().padStart(2, '0'));

        incrementHours();
      } else if (newMinutes < 0) {
        setMinutes('00');
      } else {
        setMinutes(newMinutes.toString().padStart(2, '0'));
      }
    }
  };

  const decrementTime = (type: 'hours' | 'minutes') => () => {
    if (type === 'hours') {
      if (is24HourFormat) {
        setHours((hours + 23) % 24);
        return;
      }

      const newHours = hours - 1;

      /**
       * If going from 12 to 11 or going from 12 to 11, change period
       */
      if (newHours === 11 && hours === 12) {
        changePeriod();
      }

      setHours((prev) => (prev > 1 ? prev - 1 : 12));
    } else {
      const newMinutes = parseInt(minutes, 10) - DEFAULT_MINUTE_INCREMENT;

      if (is24HourFormat) {
        if (newMinutes < 0) {
          setMinutes((60 + newMinutes).toString().padStart(2, '0'));
          setHours((hours + 23) % 24);
        } else {
          setMinutes(newMinutes.toString().padStart(2, '0'));
        }

        return;
      }

      if (newMinutes < 0 && hours > 1) {
        setMinutes((60 + newMinutes).toString().padStart(2, '0'));

        const newHours = hours - 1;

        /**
         * If going from 12 to 11 or going from 12 to 11, change period
         */
        if (newHours === 11 && hours === 12) {
          changePeriod();
        }

        setHours(hours - 1);
      } else if (newMinutes < 0) {
        setHours(12);
        setMinutes((60 + newMinutes).toString().padStart(2, '0'));
      } else {
        setMinutes(newMinutes.toString().padStart(2, '0'));
      }
    }
  };

  const changePeriod = () => {
    setPeriod(period === 'am' ? 'pm' : 'am');
  };

  return (
    <Dialog
      title={'Update time'}
      onClose={onCancel}
      actions={[
        <TextButton key="time-selector-clear-button" text="Clear" onClick={handleClear} />,
        <ElevatedButton
          key="time-selector-submit-button"
          text="Submit"
          onClick={handleSubmitTime}
        />,
      ]}
    >
      <div className={styles['time-selector-container']}>
        <div className={styles['time-input-container']}>
          <div className={styles['up-arrow-container']} onClick={incrementTime('hours')}>
            <ArrowDown />
          </div>

          <input
            className={styles['time-input']}
            type="text"
            maxLength={2}
            value={hours}
            onChange={handleTimeChange('hours')}
          />

          <div className={styles['down-arrow-container']} onClick={decrementTime('hours')}>
            <ArrowDown />
          </div>
        </div>

        <span className={styles['time-separator']}>:</span>

        <div className={styles['time-input-container']}>
          <div className={styles['up-arrow-container']} onClick={incrementTime('minutes')}>
            <ArrowDown />
          </div>

          <input
            className={styles['time-input']}
            type="text"
            value={minutes}
            onChange={handleTimeChange('minutes')}
          />

          <div className={styles['down-arrow-container']} onClick={decrementTime('minutes')}>
            <ArrowDown />
          </div>
        </div>

        {!is24HourFormat && (
          <div className={styles['time-input-container']}>
            <div className={styles['up-arrow-container']} onClick={changePeriod}>
              <ArrowDown />
            </div>

            <input
              className={styles['time-input']}
              type="text"
              maxLength={2}
              value={period}
              readOnly
            />

            <div className={styles['down-arrow-container']} onClick={changePeriod}>
              <ArrowDown />
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};
